'use client';

import { useState } from 'react';
import { useSearchParams } from 'next/navigation';
import { useAuthActions } from '@convex-dev/auth/react';
import { useQuery } from 'convex/react';
import { useLocale, useTranslations } from 'next-intl';
import { Loader2, ShieldCheck, UserPlus } from 'lucide-react';
import { api } from '../../../convex/_generated/api';
import { TERMS_PATH, PRIVACY_PATH } from '@/lib/legal';

// ============================================================================
// CREAZIONE ACCOUNT NELL'INSTALLAZIONE DEL CLIENTE.
//
// Due sole occasioni, e sono tutte e due controllate anche dal backend
// (convex/auth.ts, createOrUpdateUser):
//  1. la prima accensione, quando nel database non c'e' nessuno: chi si registra
//     diventa il padrone dell'installazione;
//  2. chi arriva con un invito aperto a suo nome, creato da un amministratore
//     dalla pagina Squadra.
// In tutti gli altri casi si vede il rimando al login. Qui si evita solo di
// mostrare un modulo che verrebbe rifiutato: il controllo vero e' nel backend.
// ============================================================================

const INPUT =
  'w-full rounded-[10px] border border-stone-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 px-4 py-[11px] text-[15px] outline-none transition-colors focus:border-[var(--accent)]';

/** Estrae il codice invito da un redirect tipo /invite/<codice> o /it/invite/<codice>. */
function inviteTokenFrom(redirect: string | null): string | null {
  if (!redirect) return null;
  const match = redirect.match(/\/invite\/([^/?#]+)/);
  return match ? decodeURIComponent(match[1]) : null;
}

export function LocalSignUp() {
  const t = useTranslations('Auth.local');
  const tSignup = useTranslations('Auth.signup');
  const locale = useLocale();
  const { signIn } = useAuthActions();
  const params = useSearchParams();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tos, setTos] = useState(false);

  const redirect = params.get('redirect');
  const token = inviteTokenFrom(redirect);

  const needsFirstAdmin = useQuery(api.setup.needsFirstAdmin);
  // L'invito si legge senza essere collegati: e' il suo scopo.
  const invite = useQuery(api.invitations.getByToken, token ? { token } : 'skip');

  const loading = needsFirstAdmin === undefined || (token !== null && invite === undefined);
  const invited = invite != null;
  const canSignUp = needsFirstAdmin === true || invited;

  async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (busy) return;

    if (!tos) {
      setError(tSignup('tosError'));
      return;
    }

    const form = new FormData(event.currentTarget);
    const password = String(form.get('password') ?? '');
    if (password.length < 8) {
      setError(t('shortPassword'));
      return;
    }
    if (password !== String(form.get('password2') ?? '')) {
      setError(t('mismatch'));
      return;
    }

    setBusy(true);
    setError(null);
    form.delete('password2');
    form.set('flow', 'signUp');
    // Con l'invito l'indirizzo non e' negoziabile: e' quello a cui l'invito e'
    // intestato, altrimenti il backend rifiuta la creazione.
    form.set('email', (invited ? invite.email : String(form.get('email') ?? '')).trim().toLowerCase());

    try {
      await signIn('password', form);
      // Con l'invito si torna alla pagina dell'invito, che crea la membership.
      window.location.href = invited && redirect ? redirect : '/app/new-workspace';
    } catch (err) {
      setError(err instanceof Error ? err.message : t('failed'));
      setBusy(false);
    }
  }

  if (loading) {
    return <div className="h-10 w-40 rounded bg-stone-200 dark:bg-zinc-800 animate-pulse" />;
  }

  if (!canSignUp) {
    return (
      <div className="w-full max-w-[380px]">
        <h1 className="text-[24px] font-bold tracking-tight m-0 mb-2">{t('closedTitle')}</h1>
        <p className="text-[14.5px] leading-[1.6] text-stone-500 dark:text-zinc-400 m-0 mb-6">
          {t('closedBody')}
        </p>
        <a
          href="/login"
          className="inline-flex items-center gap-2 px-6 py-[13px] text-[15px] font-semibold rounded-[10px] bg-stone-950 text-stone-50 dark:bg-zinc-50 dark:text-zinc-950 hover:opacity-90 transition-opacity"
        >
          {t('goToLogin')}
        </a>
      </div>
    );
  }

  return (
    <div className="w-full max-w-[380px]">
      <div
        className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-[12px] font-medium mb-5"
        style={{ background: 'var(--accent-soft)', color: 'var(--accent-strong)' }}
      >
        {invited ? <UserPlus size={14} /> : <ShieldCheck size={14} />}
        {invited ? t('invitedBadge') : t('firstBadge')}
      </div>

      <h1 className="text-[26px] font-bold tracking-tight m-0 mb-1.5">
        {invited ? t('invitedTitle') : t('firstTitle')}
      </h1>
      <p className="text-[14.5px] leading-[1.6] text-stone-500 dark:text-zinc-400 m-0 mb-7">
        {invited ? t('invitedBody', { workspace: invite.workspaceName ?? '' }) : t('firstBody')}
      </p>

      <form onSubmit={onSubmit} className="flex flex-col gap-4">
        <label className="flex flex-col gap-1.5">
          <span className="text-[13px] font-medium text-stone-600 dark:text-zinc-300">{t('name')}</span>
          <input name="name" type="text" required autoComplete="name" className={INPUT} />
        </label>

        <label className="flex flex-col gap-1.5">
          <span className="text-[13px] font-medium text-stone-600 dark:text-zinc-300">{t('email')}</span>
          {invited ? (
            <input
              type="email"
              value={invite.email}
              readOnly
              className={`${INPUT} opacity-70 cursor-not-allowed`}
            />
          ) : (
            <input name="email" type="email" required autoComplete="username" className={INPUT} />
          )}
        </label>

        <label className="flex flex-col gap-1.5">
          <span className="text-[13px] font-medium text-stone-600 dark:text-zinc-300">{t('password')}</span>
          <input name="password" type="password" required autoComplete="new-password" className={INPUT} />
        </label>

        <label className="flex flex-col gap-1.5">
          <span className="text-[13px] font-medium text-stone-600 dark:text-zinc-300">{t('password2')}</span>
          <input name="password2" type="password" required autoComplete="new-password" className={INPUT} />
        </label>

        {/* Anche sull'installazione del cliente le condizioni si accettano.
            Qui il modulo e' nostro, quindi la casella sta dove deve stare:
            dentro la registrazione, prima del tasto. */}
        <label className="flex items-start gap-2.5 cursor-pointer mt-1">
          <input
            type="checkbox"
            checked={tos}
            onChange={(e) => { setTos(e.target.checked); setError(null); }}
            className="mt-[3px] size-[16px] shrink-0 accent-[var(--accent)]"
          />
          <span className="text-[13px] leading-[1.5] text-stone-600 dark:text-zinc-300">
            {tSignup.rich('tosText', {
              terms: (c) => (
                <a href={`${locale === 'en' ? '' : `/${locale}`}${TERMS_PATH}`} target="_blank" rel="noreferrer"
                   className="underline underline-offset-2" style={{ color: 'var(--accent-strong)' }}>{c}</a>
              ),
              privacy: (c) => (
                <a href={`${locale === 'en' ? '' : `/${locale}`}${PRIVACY_PATH}`} target="_blank" rel="noreferrer"
                   className="underline underline-offset-2" style={{ color: 'var(--accent-strong)' }}>{c}</a>
              ),
            })}
          </span>
        </label>

        {error && <p className="text-[13.5px] text-red-600 dark:text-red-400 m-0">{error}</p>}

        <button
          type="submit"
          disabled={busy || !tos}
          className="inline-flex items-center justify-center gap-2 px-6 py-[13px] text-[15px] font-semibold rounded-[10px] bg-stone-950 text-stone-50 dark:bg-zinc-50 dark:text-zinc-950 hover:opacity-90 transition-opacity disabled:opacity-60 mt-1"
        >
          {busy && <Loader2 size={15} className="animate-spin" />}
          {busy ? t('creating') : invited ? t('createAccount') : t('createAdmin')}
        </button>
      </form>

      <p className="text-[12.5px] text-stone-400 dark:text-zinc-500 mt-5 m-0 leading-[1.55]">
        {invited ? t('invitedNote') : t('firstNote')}
      </p>
    </div>
  );
}
